import React from "react"

import Layout from "@/components/Layout"
import SEO from "@/components/SEO"
import { StaticImage } from "gatsby-plugin-image"
import BackgroundSection from "@/components/BackgroundSection"
import Quote from "@/components/Quote"
import { routes } from "@/utils/routes"
import { graphql, Link } from "gatsby"
import WistiaPlayer from "@/components/WistiaPlayer"

type PageProps = {
  data: any
}

export const pageQuery = graphql`
  query SocialMobilityQuery {
    site {
      siteMetadata {
        title
      }
    }
  }
`

const SocialMobility: React.FC<PageProps> = ({ data }) => {
  return (
    <Layout
      sectionOne={
        <>
          <div className="relative h-[67vh] md:min-h-[75vh]">
            <StaticImage
              src="../../static/images/transformative-bursaries-top.jpg"
              className="absolute inset-0 w-full h-full object-cover -z-50"
              alt="Transformative Bursaries Header"
            />
            <div className="absolute inset-0 bg-[#131312]/50 -z-40"></div>

            <div className="container text-white h-[67vh] md:min-h-[75vh] flex items-center px-4">
              <div className="pl-2 md:pl-6 lg:pl-10 max-w-[1000px] translate-y-10 md:translate-y-16 lg:translate-y-20">
                <div className="pb-4">
                  <h1
                    className="text-left text-white text-[300%] sm:text-[400%] md:text-[531.25%] leading-[1] font-bold normal-case tracking-normal font-gilroy"
                  >
                    Transformative Bursaries
                  </h1>
                  <div className="mt-5 h-[8px] sm:h-[10px] w-[200px] sm:w-[320px] bg-primary"></div>
                </div>
              </div>
            </div>
          </div>

          {/* Gold four-option bar below the fold */}
          <div className="bg-primary w-full h-[33vh] md:h-auto md:block">
            <div className="flex flex-col h-full w-full md:grid md:grid-cols-4 md:gap-0 md:items-stretch md:min-h-[84px]">
              <a href={routes.socialMobility()} className="flex-1 flex items-center justify-center px-4 text-center uppercase font-gilroy font-bold text-[#131312] border-b border-white text-[19px] leading-[32.3px] md:border-b-0 md:border-r md:px-2">
                Transformative Bursaries
              </a>
              <a href={routes.southWingDevelopment()} className="flex-1 flex items-center justify-center px-4 text-center uppercase font-gilroy font-bold text-[#131312] border-b border-white text-[19px] leading-[32.3px] md:border-b-0 md:border-r md:px-2">
                The South Wing
              </a>
              <a href={routes.pledge()} className="flex-1 flex items-center justify-center px-4 text-center uppercase font-gilroy font-bold text-[#131312] border-b border-white text-[19px] leading-[32.3px] md:border-b-0 md:border-r md:px-2">
                Sponsor a Paviour
              </a>
              <a href={routes.donate()} className="flex-1 flex items-center justify-center px-4 text-center uppercase font-gilroy font-bold text-[#131312] text-[19px] leading-[32.3px] md:px-2">
                Donate
              </a>
            </div>
          </div>
        </>
      }
    >
      <SEO title="Transformative Bursaries | RBAI Foundation" />

      <div className="pt-24 pb-8">
        <div className="container">
          <h2 className="text-[24px] sm:text-[28px] md:text-[32px] font-gilroy font-bold leading-[0.9] pb-4 mb-8">
            At a time of economic uncertainty for many families, <span className="text-primary">the provision of bursaries is central to RBAI's ethos</span> and will ensure that <span className="text-primary">no eligible pupil loses the opportunity</span> of an education at the School.
          </h2>

          <div className="grid gap-8 mt-16">
            <div className="w-full max-w-full overflow-hidden">
              <WistiaPlayer
                videoStyle={{
                  width: "100%",
                  height: "100%",
                }}
                videoID="wkpe9urjrb"
                className="aspect-video w-full"
              />
            </div>

            <div className="md:mt-8 mb-8 flex justify-end">
              <Link
                to={routes.donate()}
                className="inline-block bg-primary text-dark px-8 py-4 rounded-sm font-gilroy font-bold text-xl hover:bg-[#d9a01d] transition-colors text-center"
              >
                DONATE NOW
              </Link>
            </div>
          </div>

        </div>
      </div>

      <BackgroundSection
        paragraphs={
          <>
            <p className="font-gilroy-light text-white">
              Education plays a crucial role in social mobility, particularly given evidence of low educational aspiration and attainment amongst boys from less advantaged backgrounds.
            </p>
            <p className="font-gilroy-light text-white">
              Whilst our fees remain at a fraction of those charged by public schools offering a similar pupil experience in Great Britain, some parents of
              prospective pupils and those already at the School have difficulty funding their son's education.
            </p>
            <p className="font-gilroy-light text-white">
              By removing financial barriers, the RBAI Bursary Programme transforms the future trajectory for pupils from less advantaged backgrounds, having an immeasurable impact on their future lives. A vibrant bursary programme reinforces our founding values, ensuring RBAI remains a diverse and inclusive educational environment.
            </p>
          </>
        }
        paragraphsClassName="max-w-[400px] ml-auto"
        bg="/images/2023/03/social-mobility.jpg"
      />

      <div className="container pt-8 pb-0">
        <div>
          <h2 className="text-[24px] sm:text-[28px] md:text-[32px] font-gilroy font-bold leading-[0.9] pb-4 mb-8 mt-6">
            How The <span className="text-primary">Bursary Programme</span> Works
          </h2>
          <div className="mb-0">
            <WistiaPlayer
              videoStyle={{
                width: "100%",
                height: "100%",
              }}
              videoID="o296sj1xlh"
              className="aspect-video"
            />
          </div>
        </div>
      </div>

      <div className="container pt-8 pb-16">
        <div>
          <h2 className="text-[24px] sm:text-[28px] md:text-[32px] font-gilroy font-bold leading-[0.9] mt-6">
            Impacting <span className="text-primary">Pupils</span>
          </h2>
          {/* <p className="mt-8">
            Developing a sustainable bursary income will:
          </p> */}
        </div>
        {/* <div className="grid gap-8 md:grid-cols-2 pt-20">
          <Feature
            description="Underwrite our commitment to the founding principles of the
                    School, ensuring that no eligible pupil loses the opportunity
                    of an education at RBAI."
            freeWidth
          />
          <Feature
            description="Maintaining RBAI as the premier school for competitive
                    swimming in Northern Ireland."
            freeWidth
          />
        </div> */}
      </div>

      <div className="container grid md:grid-cols-2 px-4">
        <Quote
          quote="At Inst, taking part is much more important than your background.
            Alongside learning, my bursary allowed me to gain so much
            confidence from the range of extra activities."
          author="Anonymous Pupil"
          className="bg-[#111111]"
        />

        <Quote
          quote="When our circumstances changed, we were extremely grateful for
            the Bursary Programme, ensuring our son’s education was not
            impacted during a challenging time."
          author="Anonymous Parent"
          className="bg-[#faba20]"
        />
      </div>
    </Layout>
  )
}

export default SocialMobility
